import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Typography, withStyles } from '@material-ui/core';
import ConfirmModal from '../../../common/ConfirmModal';
import { BUTTON_DEFAULT_COLOR } from '../../../common/Button/constants';
import styles from '../ChallengeInfo/styles';
import theme from '../../../theme';
import ChallengeActionsTable from './ChallengeActionsTable';
import { actionTablePropType } from '../../../actions/propTypes';
import { challengeAddButtonsCell } from '../../ActionsScreen/AdminActionsScreen/constants';
import { PIN_ADMIN_VISIBILITY_ALL_INCLUDED } from '../../../actions/constants';
import useStyles from './styles';

const ChallengeActionsModal = ({
    isOpen,
    onClose,
    onCancel,
    onConfirm,
    fetchActions,
    actions,
    themes,
    onAddAction,
    shouldConfirm,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();

    // New state for confirmation modal
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const handleAddAction = useCallback(
        (action) => {
            if (shouldConfirm) {
                setPendingAction(action);
                setShowConfirmation(true);
            } else {
                onAddAction(action);
            }
        },
        [shouldConfirm, onAddAction],
    );

    const onButtonsCell = useCallback(
        (id, key) => {
            switch (key) {
                case 'editChallenge': {
                    const actionToAdd = actions?.data?.find(
                        (action) => action.id === id,
                    );
                    handleAddAction(actionToAdd);
                    break;
                }
                default:
                    break;
            }
        },
        [actions, handleAddAction],
    );

    return (
        <>
            <ConfirmModal
                isOpen={isOpen}
                title={t('challenge.actions.add')}
                titleStyle={classes.title}
                modalStyle={classes.modal}
                cancelBtnType={BUTTON_DEFAULT_COLOR}
                cancelBtnText={t('cancel')}
                onClose={onClose}
                onCancel={onCancel}
                onConfirm={onConfirm}
                confirmBtnType="success"
                centeredButtons
                cancelBtnHidden
                confirmBtnColor={theme.palette.primary.main}
                fullWidth
                maxWidth="md"
            >
                {isOpen && (
                    <ChallengeActionsTable
                        themes={themes}
                        showThemesFilter
                        fetchDataFunc={fetchActions}
                        data={actions}
                        buttonsCell={challengeAddButtonsCell}
                        onButtonsCell={onButtonsCell}
                        type={PIN_ADMIN_VISIBILITY_ALL_INCLUDED}
                    />
                )}
            </ConfirmModal>

            <ConfirmModal
                isOpen={showConfirmation}
                title={t('challenge.actions.confirmAdd')}
                onClose={() => setShowConfirmation(false)}
                onCancel={() => setShowConfirmation(false)}
                onConfirm={() => {
                    onAddAction(pendingAction);
                    setShowConfirmation(false);
                    setPendingAction(null);
                }}
                cancelBtnType={BUTTON_DEFAULT_COLOR}
                cancelBtnText={t('cancel')}
                confirmBtnType="primary"
                confirmBtnText={t('confirm')}
            >
                <Typography>
                    {t('challenge.actions.confirmAddMessage')}
                </Typography>
            </ConfirmModal>
        </>
    );
};

ChallengeActionsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    fetchActions: PropTypes.func.isRequired,
    actions: actionTablePropType.isRequired,
    themes: PropTypes.array,
    onAddAction: PropTypes.func.isRequired,
    shouldConfirm: PropTypes.bool,
};

ChallengeActionsModal.defaultProps = {
    themes: [],
    shouldConfirm: false,
};

export default withStyles(styles)(ChallengeActionsModal);
